import React, { useMemo, useState } from 'react';

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const DAY_HEADERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const MAX_VISIBLE = 2;

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

// displayDate חוזר מהשרת כ-datetime UTC - חייבים להמיר לזמן מקומי של הדפדפן
// לפני גזירת YYYY-MM-DD (לא slice של המחרוזת הגולמית), אחרת ביום ב-UTC+3
// ההודעה תופיע ביום הקודם בלוח. ראו הערה זהה ב-AnnouncementsTab.jsx
const localKey = (d) => keyOf(new Date(d));

let hebrewFormatter = null;
try {
  hebrewFormatter = new Intl.DateTimeFormat('he-IL-u-ca-hebrew', { day: 'numeric', month: 'long', year: 'numeric' });
} catch { /* דפדפן בלי תמיכה בלוח עברי - פשוט לא מציגים תאריך עברי */ }
const hebrewDate = (dt) => {
  try { return hebrewFormatter ? hebrewFormatter.format(dt) : ''; } catch { return ''; }
};

// תצוגת לוח שנה חודשי להודעות/פרסומות - כל הודעה מוצגת ביום ה-displayDate שלה
// (אותו יום שבו היא מוצגת בפועל באפליקציה). לחיצה על הודעה = עריכה, לחיצה על
// יום ריק/על הרקע של יום = הודעה חדשה עם התאריך הזה מוגדר מראש
export default function AnnouncementsCalendar({ announcements, onEdit, onCreateOnDate }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [expandedKey, setExpandedKey] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const todayKey = keyOf(today);

  const byDay = useMemo(() => {
    const map = {};
    announcements.forEach((a) => {
      if (!a.displayDate) return;
      const k = localKey(a.displayDate);
      (map[k] = map[k] || []).push(a);
    });
    Object.values(map).forEach((list) => list.sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0)));
    return map;
  }, [announcements]);

  // 6 שורות קבועות (42 תאים) כולל ימי החודשים השכנים בעמום - כמו בתצוגת לוח רגילה
  const cells = useMemo(() => {
    const firstWeekday = new Date(year, month, 1).getDay();
    return Array.from({ length: 42 }, (_, i) => new Date(year, month, 1 - firstWeekday + i));
  }, [year, month]);

  const shiftMonth = (delta) => {
    setExpandedKey(null);
    setCursor(new Date(year, month + delta, 1));
  };

  return (
    <div style={s.wrap}>
      <div style={s.toolbar}>
        <button style={s.navBtn} onClick={() => shiftMonth(-1)}>חודש קודם</button>
        <div style={s.monthLabel}>{MONTH_NAMES[month]} {year}</div>
        <button style={s.navBtn} onClick={() => shiftMonth(1)}>חודש הבא</button>
        <button style={s.todayBtn} onClick={() => { setExpandedKey(null); setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); }}>היום</button>
      </div>

      <div style={s.scroller}>
        <div style={s.grid}>
          {DAY_HEADERS.map((h) => <div key={h} style={s.dayHeader}>{h}</div>)}
          {cells.map((dt) => {
            const key = keyOf(dt);
            const inMonth = dt.getMonth() === month;
            const list = byDay[key] || [];
            const hasActive = list.some((a) => a.active);
            const expanded = expandedKey === key;
            const visible = expanded ? list : list.slice(0, MAX_VISIBLE);
            const hiddenCount = list.length - visible.length;
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => onCreateOnDate(dt)}
                onKeyDown={(e) => { if (e.key === 'Enter') onCreateOnDate(dt); }}
                title="לחצו להוספת הודעה ליום הזה"
                style={{
                  ...s.cell,
                  ...(inMonth ? {} : s.cellOut),
                  ...(hasActive ? s.cellHasEvent : {}),
                  ...(key === todayKey ? s.cellToday : {}),
                }}
              >
                <div style={s.cellTop}>
                  <span style={s.dayNum}>{dt.getDate()}</span>
                  {list.length > 0 && <span style={s.dot} />}
                </div>
                <div style={s.hebrew}>{hebrewDate(dt)}</div>
                {visible.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    title={a.message || a.title}
                    onClick={(e) => { e.stopPropagation(); onEdit(a); }}
                    style={{ ...s.chip, ...(a.active ? {} : s.chipInactive) }}
                  >
                    {a.title}
                  </button>
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpandedKey(key); }}
                    style={s.moreBtn}
                  >
                    +{hiddenCount} נוספות
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={s.legend}>
        <span style={s.legendItem}><i style={{ ...s.legendChip, ...s.chipSwatch }} /> הודעה פעילה</span>
        <span style={s.legendItem}><i style={{ ...s.legendChip, ...s.chipSwatchInactive }} /> הודעה מוסתרת</span>
        <span style={s.legendText}>לחיצה על הודעה לעריכה · לחיצה על יום ליצירת הודעה חדשה בתאריך הזה</span>
      </div>
    </div>
  );
}

const s = {
  wrap:         { direction: 'rtl' },
  toolbar:      { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  monthLabel:   { fontSize: 17, fontWeight: 800, color: '#1a1f36', minWidth: 130, textAlign: 'center' },
  navBtn:       { padding: '8px 14px', background: '#fff', color: '#1a1f36', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  todayBtn:     { padding: '8px 14px', background: '#fffaf0', color: '#8a6d1a', border: '1.5px solid #d9c48a', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },

  scroller:     { overflowX: 'auto', borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', background: '#fff', padding: 14 },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(112px, 1fr))', gap: 8, minWidth: 812 },
  dayHeader:    { textAlign: 'center', fontSize: 13, color: '#8792a2', fontWeight: 800, padding: '4px 0 8px' },

  cell:         { minHeight: 92, borderRadius: 10, border: '1px solid #e8ebf0', background: '#fff', padding: '6px 7px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 3, boxSizing: 'border-box', textAlign: 'right', fontFamily: 'inherit' },
  cellOut:      { background: '#fafafa', opacity: 0.55 },
  cellHasEvent: { background: '#fdf8ea', borderColor: '#e6d3a1' },
  cellToday:    { borderColor: '#1f6feb', borderWidth: 2, padding: '5px 6px' },
  cellTop:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dayNum:       { fontSize: 14, fontWeight: 800, color: '#1a1f36' },
  dot:          { width: 8, height: 8, borderRadius: '50%', background: '#d0a93e' },
  hebrew:       { fontSize: 10.5, color: '#8792a2', lineHeight: 1.2 },

  chip:         { display: 'block', width: '100%', textAlign: 'right', background: '#f7edcf', color: '#7a5d10', border: 'none', borderRadius: 6, padding: '3px 6px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'inherit' },
  chipInactive: { background: '#eef0f3', color: '#8792a2', textDecoration: 'line-through' },
  moreBtn:      { background: 'none', border: 'none', color: '#1f6feb', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0, textAlign: 'right', fontFamily: 'inherit' },

  legend:       { display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: '#525f7f', flexWrap: 'wrap', alignItems: 'center' },
  legendItem:   { display: 'flex', alignItems: 'center', gap: 6 },
  legendChip:   { display: 'inline-block', width: 22, height: 12, borderRadius: 4 },
  chipSwatch:   { background: '#f7edcf' },
  chipSwatchInactive: { background: '#eef0f3' },
  legendText:   { color: '#8792a2' },
};
